import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { useAppSelector } from 'common/hooks/state';
import { RootState } from 'common/store/store';
import { trackEvent, MixpanelEventName } from 'common/utils/mixpanel/eventTriggers';
import { useShepherd } from 'features/Shepherd/ShepherdProvider';
import { isPrimerOpen } from 'features/NotificationPrimer/primer';
import {
  MK3_COLORS, bodyStyle, cardShellStyle, headlineStyle, primaryButtonStyle,
} from 'features/Onboarding/components/mk3/styles';
import {
  RATING_PROMPT_EVENT, RatingEventDetail, RatingTrigger, isOnboardingPath, ratingKeys, readRatingValue,
  shouldPromptRating, storeReviewUrl, writeRatingValue,
} from './ratingRules';

const ENTITLEMENT_ID = 'Doqit Pro';

// RevenueCat knows the entitlement but it is no longer live: the same
// customer the backend marks rc_subscription_status = expired. Unknown
// (offline, SDK not ready) counts as expired, so nobody lapsed is asked.
const isExpired = async (): Promise<boolean> => {
  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    const ent = customerInfo?.entitlements?.all?.[ENTITLEMENT_ID];
    return !!ent && !ent.isActive;
  } catch {
    return true;
  }
};

interface CardProps {
  onRate: () => void;
  onNotNow: () => void;
}

export const RatingPromptCard = ({ onRate, onNotNow }: CardProps) => (
  <div
    className="w-full max-w-sm rounded-3xl p-6 text-center"
    style={cardShellStyle}
    role="dialog"
    aria-modal="true"
    aria-label="Enjoying Doqit?"
    data-cy="rating-prompt"
  >
    <h2 style={headlineStyle}>Enjoying Doqit?</h2>
    <p className="mt-2 mb-5" style={bodyStyle}>
      A quick rating helps other people find us. It takes a few seconds.
    </p>
    <button
      type="button"
      onClick={onRate}
      className="w-full py-4 text-white rounded-full"
      style={primaryButtonStyle}
      data-cy="rating-prompt-rate"
    >
      Rate Doqit
    </button>
    <button
      type="button"
      onClick={onNotNow}
      className="w-full mt-3 py-3"
      style={{ ...bodyStyle, color: MK3_COLORS.accent }}
      data-cy="rating-prompt-not-now"
    >
      Not now
    </button>
  </div>
);

/**
 * Store rating ask. No in-app review plugin is installed, so this shows a
 * short Doqit card first and only opens the store's write-a-review page if
 * the person taps Rate. Native only: the web has no store to rate.
 */
export const RatingPrompt = () => {
  const location = useLocation();
  const user = useAppSelector((state: RootState) => state.auth.user) as
    { id?: string | number; user?: { id?: string | number } } | null;
  const userId = user?.user?.id ?? user?.id;
  const { activeStepId } = useShepherd();
  const [trigger, setTrigger] = useState<RatingTrigger | null>(null);
  const busyRef = useRef(false);
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const tourActiveRef = useRef(false);
  tourActiveRef.current = activeStepId !== null;

  const offer = useCallback(async (detail: RatingEventDetail) => {
    if (userId == null || busyRef.current) return;
    busyRef.current = true;
    try {
      const keys = ratingKeys(userId);
      const onboarding = isOnboardingPath(pathRef.current) || tourActiveRef.current || isPrimerOpen();
      if (onboarding) return;
      const lastPromptedAt = await readRatingValue(keys.lastPrompted);
      const expired = await isExpired();
      if (!shouldPromptRating({
        now: new Date(), lastPromptedAt, onboarding, expired,
      })) return;
      // Re-check: the tour or primer may have opened while we waited.
      if (isOnboardingPath(pathRef.current) || tourActiveRef.current || isPrimerOpen()) return;
      await writeRatingValue(keys.lastPrompted, new Date().toISOString());
      setTrigger(detail.trigger);
      trackEvent(MixpanelEventName.ratingPrompted, {
        trigger: detail.trigger, method: 'store_link', platform: Capacitor.getPlatform(),
      });
    } finally {
      busyRef.current = false;
    }
  }, [userId]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || userId == null) return undefined;
    const handler = (event: Event) => {
      const { detail } = event as CustomEvent<RatingEventDetail>;
      if (detail) offer(detail);
    };
    window.addEventListener(RATING_PROMPT_EVENT, handler);
    return () => window.removeEventListener(RATING_PROMPT_EVENT, handler);
  }, [userId, offer]);

  const handleRate = () => {
    trackEvent(MixpanelEventName.ratingStoreOpened, { trigger });
    setTrigger(null);
    const url = storeReviewUrl(Capacitor.getPlatform());
    // Capacitor hands non-web schemes (itms-apps, market) to the OS.
    if (url) window.location.href = url;
  };

  const handleNotNow = () => {
    trackEvent(MixpanelEventName.ratingDeclined, { trigger });
    setTrigger(null);
  };

  if (!trigger) return null;
  return (
    <div
      className="fixed inset-0 flex items-center justify-center px-4"
      style={{ zIndex: 9980, background: 'rgba(57,38,79,0.45)' }}
    >
      <RatingPromptCard onRate={handleRate} onNotNow={handleNotNow} />
    </div>
  );
};

export default RatingPrompt;
